// Pure helpers for translating tenant-action API responses into the
// kind of feedback the admin actually needs. Kept out of App.tsx so
// the response shapes are unit-tested without React in the loop.
//
// All admin tenant actions (suspend, terminate, refund, credit) flow
// through the same admin-bff approval workflow. The wire shape is
// identical: HTTP 200 means auto-executed, HTTP 202 means pending
// approval — both deserialize into the same envelope below. The
// helpers below produce the right operator-facing message per action.

export interface ApprovalEnvelope {
  status?: string;
  approvalId?: string;
  requiredRole?: string;
  twoPersonRule?: boolean;
  expiresAt?: string;
  // Auto-executed shape carries downstream-service fields (id, refund
  // status, error, etc) — surfaced verbatim where useful.
  id?: string;
  // Refund/credit auto-execute returns AdminBillingActionResult shape:
  stripeId?: string;
  stripeChargeId?: string;
  error?: string;
}

export type ActionKind =
  | 'suspend'
  | 'terminate'
  | 'refund'
  | 'credit'
  | 'plan_override'
  | 'clear_plan_override';

export interface ActionOutcome {
  kind: 'executed' | 'pending' | 'failed';
  message: string;
  /** Whether the caller should refetch the tenant list (only on executed). */
  refetch: boolean;
  approvalId?: string;
}

const ACTION_LABELS: Record<ActionKind, { verb: string; pendingNoun: string; doneNoun: string }> = {
  suspend: { verb: 'Suspend', pendingNoun: 'Suspend', doneNoun: 'Tenant suspended.' },
  terminate: {
    verb: 'Terminate',
    pendingNoun: 'Termination',
    doneNoun: 'Tenant termination scheduled.',
  },
  refund: { verb: 'Refund', pendingNoun: 'Refund', doneNoun: 'Refund issued.' },
  credit: { verb: 'Credit', pendingNoun: 'Credit', doneNoun: 'Credit applied.' },
  plan_override: {
    verb: 'Override plan',
    pendingNoun: 'Plan override',
    doneNoun: 'Plan override applied.',
  },
  clear_plan_override: {
    verb: 'Clear override',
    pendingNoun: 'Clear plan override',
    doneNoun: 'Plan override cleared.',
  },
};

export function interpretActionResult(
  action: ActionKind,
  res: ApprovalEnvelope,
): ActionOutcome {
  const labels = ACTION_LABELS[action];

  if (res?.status === 'pending_approval' && res.approvalId) {
    const role = res.requiredRole ?? 'a senior admin';
    const twoPerson = res.twoPersonRule
      ? ' Two-person rule: a different approver must sign off.'
      : '';
    return {
      kind: 'pending',
      approvalId: res.approvalId,
      refetch: false,
      message: `${labels.pendingNoun} pending approval — needs ${role}.${twoPerson} Open the Approvals page to track it.`,
    };
  }

  // Refund/credit can auto-execute but the downstream Stripe call may
  // still have failed — surface that as a 'failed' outcome instead of
  // celebrating "done" on a non-event.
  if ((action === 'refund' || action === 'credit') && res?.status === 'failed') {
    return {
      kind: 'failed',
      refetch: false,
      message: `${labels.verb} recorded but Stripe call failed: ${res.error ?? 'unknown error'}. Review in admin audit.`,
    };
  }

  return {
    kind: 'executed',
    // Plan override changes the effective plan, which the tenant list
    // displays, so refetch alongside suspend/terminate. Refund/credit
    // don't change tenant.status or plan so leave the list alone.
    refetch:
      action === 'suspend' ||
      action === 'terminate' ||
      action === 'plan_override' ||
      action === 'clear_plan_override',
    message: labels.doneNoun,
  };
}

// Backwards compat re-export so existing callers that import
// interpretSuspendResult keep working without churn.
export const interpretSuspendResult = (res: ApprovalEnvelope): ActionOutcome =>
  interpretActionResult('suspend', res);
